<script setup>
import {ref, computed, watch, onMounted, onBeforeUnmount} from 'vue';
import FixtureMatrixCell from "@/components/leagues/FixtureMatrixCell";
import TeamCard from "@/components/teams/TeamCard.vue";
import {useAuthService} from "@/composables/useAuthService"
const {notify, addListener, removeListener, removeLocalListenersWithEvent} = useListenerService();

const model = defineModel();
const fullscreen = defineModel('fullscreen',{ default : () => false})
const hoverRow = ref(null);
const hoverCol = ref(null);
const {user} = useAuthService();
import {useHandicaps} from '~/composables/useHandicaps';
const {labelFor: handicapLabelFor, chipClassFor} = useHandicaps();
const rowHeadWidth = computed(() => props.handicapSettings ? 240 : 170);

const props = defineProps({
  simple: false,
  closed: {
    type: Boolean,
    default: false
  },
  highlightedTeamIds: {
    type: Array,
    default: () => []
  },
  knockoutTeamIds: {
    type: Array,
    default: () => []
  },
  // Sport-gated, resolved by TournamentBracket — a stage carries no sport of
  // its own. Off unless told.
  showSquashLevel: {
    type: Boolean,
    default: false
  },
  // Team handicap labels come from the tournament's rendered settings (null = off).
  handicapSettings: {
    type: Object,
    default: null
  }
});

// Listen for fixture updates to refresh stage data
onMounted(() => {
  addListener('fixtureUpdated',(fixture) => {
    if(fixture != null && fixture.tournamentStageId === model.value.id) {
      notify('tournamentStageUpdated', model.value.id);
    }
  });
});

onBeforeUnmount(() => {
  removeLocalListenersWithEvent('fixtureUpdated');
});

const currentUserTeamIds = computed(() =>
    model.value.teams
        ?.filter(t => t.players?.map(p => p.id).includes(user.value?.playerId))
        .map(t => t.id) || []
);

const fixture = (homeTeam, awayTeam) => {
  const fixture = homeTeam.fixtures?.find(f => f.opponentTeamId === awayTeam.id);
  if (fixture) {
    return {
      ...fixture,
      tournamentStageId: fixture.tournamentStageId || model.value.id
    };
  }
  return {
    scoringSystem: model.value.scoringSystem,
    tournamentStageId: model.value.id,
    tournamentStageName: model.value.name,
    homeTeamId: homeTeam.id,
    awayTeamId: awayTeam.id,
  };
};

function isEqualToPrevOrNext(index) {
  const teams = model.value.teams
  const current = teams[index]
  if (!current) return false

  const prev = index > 0 ? teams[index - 1] : null
  const next = index < teams.length - 1 ? teams[index + 1] : null

  return (prev && prev.position === current.position) ||
      (next && next.position === current.position)
}

</script>
<template>
  <div :class="fullscreen ? 'fullscreen tournament-stage-container' : 'tournament-stage-container'">
    <v-alert v-if="fullscreen" @click="fullscreen = false"
             variant="tonal"
             color="error"
             class="pa-3 font-weight-bold mb-5 text-center cursor rounded-0">
      <v-icon icon="mdi-close"></v-icon>
      Close fullscreen
    </v-alert>
    <div :class="{'table-wrapper' : true, 'border-bottom' : !simple}">
      <v-table :hover="true" v-if="model.teams && model.teams.length > 0">
        <template v-slot:default>
          <tr class="matrix-head-row">
            <th v-if="simple" class="pa-1 pt-3"></th>
            <th :class="simple ? '' : 'sticky-column'"></th>
            <th v-if="!simple" class="pa-1 pt-3 matrix-head-label">Pts</th>
            <th v-if="!simple"  class="pa-1 pt-3 text-center" v-for="(teamHeader, index) in model.teams"
                :key="'header'+teamHeader.id"
                :class="{'cell-hover': hoverCol === index}">
              <div class="d-flex justify-center">
                <team-card :minimized="true" :max-lines="2" :show-squash-level="false" :team="teamHeader"/>
              </div>
            </th>
            <th class="pa-1 pt-3"></th>
            <th class="sticky-column-right pa-1 pt-3 matrix-head-label">Pts</th>
          </tr>
          <tr v-for="(teamRow, teamIndex) in model.teams" :key="teamRow.id"
              :class="highlightedTeamIds.indexOf(teamRow.id) !== -1 ? 'highlighted' : ''"
              @mouseover="hoverRow = teamIndex" @mouseleave="hoverRow = null">
            <td v-if="simple" style="width:50px; min-width: 50px; max-width: 50px;" class="text-center border-light-right"
                :class="{'cell-hover': hoverRow === teamIndex}">
              <div class="d-flex align-center justify-center">
                <span v-if="isEqualToPrevOrNext(teamIndex)" class="mr-1 pos-equal">=</span>
                <span class="pos-badge">{{ teamRow.position }}</span>
              </div>
            </td>
            <!-- the row head grows when handicap chips sit beside the names, or they spill into the grid -->
            <td :style="!simple ? `width:${rowHeadWidth}px; min-width: ${rowHeadWidth}px; max-width: ${rowHeadWidth}px;` : ''" class="py-2 ps-3 pe-3 matrix-rowhead"
                :class="{'cell-hover': hoverRow === teamIndex, 'sticky-column': !simple}">
              <div class="d-flex align-center">
                <div class="flex-fill">
                  <team-card :highlight-color="hoverCol === teamIndex ? 'success' : 'info'"
                             :max-lines="2"
                             :handicap-label="handicapLabelFor(handicapSettings, teamRow.handicap)"
                             :handicap-tone="chipClassFor(handicapSettings, teamRow.handicap)"
                             :highlighted="(hoverCol === teamIndex || hoverRow === teamIndex) && hoverCol !== hoverRow && hoverCol !== null"
                             :show-squash-level="showSquashLevel"
                             :team="teamRow"/>
                </div>
                <v-badge
                  v-if="knockoutTeamIds.includes(teamRow.id) || highlightedTeamIds.includes(teamRow.id)"
                  :inline="true"
                  :dot="true"
                  :color="knockoutTeamIds.includes(teamRow.id) ? 'success' : 'info'"
                  :class="knockoutTeamIds.includes(teamRow.id) ? 'progressed-dot' : 'highlight-dot'"
                />
              </div>
            </td>
            <td v-if="!simple" style="width:50px; min-width: 50px; max-width: 50px;" class="font-weight-bold text-center"
                :class="{'cell-hover': hoverRow === teamIndex}">
              {{ teamRow.totalPoints }}

            </td>
            <fixture-matrix-cell v-if="!simple"  v-for="(teamColumn, colIndex) in model.teams"
                                 :closed="closed"
                                 :class="{'cell-hover': hoverCol === colIndex || hoverRow === teamIndex, 'cell-hover-both' : hoverCol === colIndex && hoverRow === teamIndex}"
                                 @mouseover="hoverCol = colIndex" @mouseleave="hoverCol = null"
                                 :key="teamRow.id + '' + teamColumn.id"
                                 :tournament-stage-id="model.id"
                                 :scoring-system="model.scoringSystem"
                                 :current-user-team-ids="currentUserTeamIds"
                                 :club-id="model.clubId"
                                 :row-team="teamRow"
                                 :column-team="teamColumn"
                                 :fixture="fixture(teamRow,teamColumn)"></fixture-matrix-cell>
            <td :class="{'cell-hover': hoverRow === teamIndex}">
            </td>
            <td style="min-width: 50px; max-width: 50px; width: 50px;"
                class="font-weight-bold text-center sticky-column-right"
                :class="{'cell-hover': hoverRow === teamIndex}">
              {{ teamRow.totalPoints }}
            </td>
          </tr>
        </template>
      </v-table>
      <div v-else class="pa-3" style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;">
        No entrants in this group yet.
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
td, th {
  border-spacing: 0 0.5rem;
}

.matrix-head-row th {
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.matrix-head-label {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.pos-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: rgb(var(--v-theme-light));
  font-size: 11.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
}

.pos-equal {
  font-size: 11.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
}

table {
  border-collapse: collapse;
  width: 100%;
}

.sticky-column {
  position: sticky;
  left: 0;
  background-color: rgb(var(--v-theme-surface));
  z-index: 1;
  border-right: solid 1px rgb(var(--v-theme-borderLight));
}

.sticky-column-right {
  position: sticky;
  right: 0;
  background-color: rgb(var(--v-theme-surface));
  z-index: 1;
  border-left: solid 1px rgb(var(--v-theme-borderLight));
}

.cell-hover {
  background-color: rgb(var(--v-theme-light));
  &.cell-hover-both {
    background-color: rgb(var(--v-theme-surface));
  }
}

.fullscreen {
  background: #fff;
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  overflow: auto;
}

td {
  .highlight-dot {
    display: none;
  }
  .progressed-dot {
    display: inline-flex; // Always show green dots for progressed teams
  }
}

.highlighted {
  td {
    .highlight-dot {
      display: inline-flex;
    }
  }
}

/* phones: keep the row head narrow — the chip wraps under the name (TeamCard's own phone rule) */
@media (max-width: 600px) {
  .matrix-rowhead {
    width: 150px !important;
    min-width: 150px !important;
    max-width: 150px !important;
    overflow: hidden;
  }
}
</style>
